import PostUserLoginResponse from "@/models/api/user/PostUserLoginResponse";
import PostUserLoginRequest from "@/models/api/user/PostUserLoginRequest";
/*eslint  @typescript-eslint/no-use-before-define: 0*/

import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "../index";
import { getOfficeFormOfPayments, getOfficeSettings, getPermissions, postLogin, postLogout } from "@/requests/user";
import { removeAuthAxios, setAuthAxios } from "@/plugins/axios";
import routerInstance from "@/plugins/router";
import getOfficeNameFromCurrentHref, { setOfficeNameIntoCurrentHref } from "@/utils/getParamsFromPath";
import jwtDecode from "jwt-decode";
import { JwtPayload } from "@/models/api/JwtPayload";
import { OfficeSettings } from "@/models/api/OfficeSettings";
import { FormOfPaymentItem } from "@/models/api/FormOfPaymentItem";
import {
  getUserInfo,
  changePassword,
  changeEmail,
  changeOfficeProfile,
  changeCulture,
  changeUncheckBookingAfterExit,
  saveUserSystemDate,
} from "@/requests/administration/administrationUsers";
import { UserBasicInfo } from "@/models/api/administration/users/UserBasicInfo";
import { UserOfficeProfile } from "@/models/api/administration/users/UserOfficeProfile";
import i18n from "@/plugins/i18n";
import { apex } from "@/plugins/apexcharts";
import { SnackbarModel } from "@/models/components/popup/Snackbar";
import { FORM_OF_PAYMENT_TYPE } from "@/static/formOfPaymentType";

const DEFAULT_LOGGED_ROUTE = { name: "About" };

export { DEFAULT_LOGGED_ROUTE };

export interface UserStateInterface {
  permissions: Record<string, Array<number>>;
  tokens: Record<string, string | null>;
  officeSettings: OfficeSettings | null;
  formOfPayments: null | Array<FormOfPaymentItem>;
  userBasicInfo: UserBasicInfo;
  userOfficeProfiles: Array<UserOfficeProfile>;
  preserveFiltersState: boolean;
  showDocumentsAfterGeneration: boolean;
}

const createTokenStorageKey = (officeShortName: string): string => "LOGIN_TOKEN: " + officeShortName;

const storeToken = (token: string, officeShortName: string) => {
  localStorage.setItem(createTokenStorageKey(officeShortName), token);
};

export const UserState: UserStateInterface = {
  permissions: {},
  tokens: {},
  officeSettings: null,
  formOfPayments: null,
  userBasicInfo: new UserBasicInfo(),
  userOfficeProfiles: [],
  preserveFiltersState: false,
  showDocumentsAfterGeneration: false,
};

const userModule = defineModule({
  state: UserState as UserStateInterface,
  namespaced: true as const,
  getters: {
    isLogged(...args): boolean {
      const { state } = _moduleGetterContext(args);
      const companyName = getOfficeNameFromCurrentHref();
      return typeof state.tokens[companyName] === "string";
    },
    isPermissionsDownloaded(...args): boolean {
      const { state } = _moduleGetterContext(args);
      const companyName = getOfficeNameFromCurrentHref();
      return state.permissions[companyName] !== undefined && state.permissions[companyName].length > 0;
    },
    username(...args): string {
      const { state, getters } = _moduleGetterContext(args);
      if (getters.isLogged) {
        const companyName = getOfficeNameFromCurrentHref();
        const token = state.tokens[companyName] as string;
        const decodedToken = jwtDecode(token) as JwtPayload;
        return decodedToken.UserLogin;
      }
      return "";
    },
  },
  mutations: {
    SET_PERMISSIONS(state, permissions: Array<number>) {
      const officePermissions: Record<string, Array<number>> = {};
      officePermissions[getOfficeNameFromCurrentHref()] = permissions;
      state.permissions = { ...state.permissions, ...officePermissions };
      // do not change it!, done due to re-evaluation
    },
    SET_TOKEN(state, token: string | null) {
      const officeNameToken: Record<string, string | null> = {};
      officeNameToken[getOfficeNameFromCurrentHref()] = token;
      state.tokens = { ...state.tokens, ...officeNameToken };
      // must be state.tokens = sth due to re-evaluate getters
      // do not change it!
    },
    SET_OFFICE_SETTINGS(state, param: OfficeSettings) {
      state.officeSettings = param;
    },
    SET_FORM_OF_PAYMENTS(state, params: Array<FormOfPaymentItem>) {
      state.formOfPayments = params;
    },
    SET_USER_BASIC_INFO(state, param: UserBasicInfo) {
      state.userBasicInfo = param;
    },
    SET_USER_PROFILES(state, param: Array<UserOfficeProfile>) {
      state.userOfficeProfiles = param;
    },
    SET_PRESERVE_FILTERS_STATE(state, param: boolean) {
      state.preserveFiltersState = param;
    },
    SET_SHOW_DOCUMENTS_AFTER_GENERATION(state, param: boolean) {
      state.showDocumentsAfterGeneration = param;
    },
  },
  actions: {
    hasOneOfPermission(context, permissionsArray: Array<number>): boolean {
      const { state } = _moduleActionContext(context);
      return (
        state.permissions[getOfficeNameFromCurrentHref()].filter((element) => permissionsArray.includes(element))
          .length > 0
      );
    },
    hasPermission(context, permissionId: number): boolean {
      const { state } = _moduleActionContext(context);
      return state.permissions[getOfficeNameFromCurrentHref()].includes(permissionId);
    },
    async restoreToken(context): Promise<string | null> {
      const { commit } = _moduleActionContext(context);
      const officeShortName = getOfficeNameFromCurrentHref();
      const token = localStorage.getItem(createTokenStorageKey(officeShortName));
      commit.SET_TOKEN(token);
      if (typeof token === "string") {
        setAuthAxios(token);
      }
      return token;
    },
    async downloadPermissions(context) {
      const { commit, dispatch } = _moduleActionContext(context);
      // noinspection ES6MissingAwait
      dispatch.downloadOfficeSettings();
      const response = await getPermissions();
      if (response.status == 200) {
        commit.SET_PERMISSIONS(response.data);
      }
    },
    async downloadOfficeSettings(context) {
      const { commit } = _moduleActionContext(context);
      const response = await getOfficeSettings();
      i18n.locale = response.userCulture;
      apex.setLocale(response.userCulture);
      localStorage.setItem("locales", response.userCulture);
      commit.SET_OFFICE_SETTINGS(response);
    },
    async downloadOfficeFormOfPayments(context, type: FORM_OF_PAYMENT_TYPE) {
      const { commit, state } = _moduleActionContext(context);
      if (state.formOfPayments === null) {
        const response = await getOfficeFormOfPayments(type);
        commit.SET_FORM_OF_PAYMENTS(response);
      }
    },
    async downloadUserInfo(context) {
      const { commit } = _moduleActionContext(context);
      const userBasicInfo = await getUserInfo();
      commit.SET_USER_BASIC_INFO(userBasicInfo.basicInfo);
      commit.SET_USER_PROFILES(userBasicInfo.userOfficeProfiles);
    },
    async changePassword(context, param: { old: string; new: string }) {
      const { dispatch, rootDispatch } = _moduleActionContext(context);
      await changePassword(param.old, param.new);
      dispatch.downloadUserInfo();
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
    async changeEmail(context, email: string) {
      const { dispatch, rootDispatch } = _moduleActionContext(context);
      await changeEmail(email);
      dispatch.downloadUserInfo();
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
    async changeCulture(context, culture: string) {
      const { dispatch, rootDispatch } = _moduleActionContext(context);
      await changeCulture(culture);
      dispatch.downloadOfficeSettings();
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
    async changeOfficeProfile(context, officeProfileId: number) {
      const { dispatch, rootDispatch } = _moduleActionContext(context);
      await changeOfficeProfile(officeProfileId);
      dispatch.downloadUserInfo();
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
    async saveUncheckBookingAfterExit(context, uncheckBookingAfterExit) {
      const { dispatch, rootDispatch } = _moduleActionContext(context);
      await changeUncheckBookingAfterExit(uncheckBookingAfterExit);
      dispatch.downloadOfficeSettings();
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
    async saveUserSystemTime(context, date: string) {
      const { dispatch, rootDispatch } = _moduleActionContext(context);
      await saveUserSystemDate(date);
      dispatch.downloadOfficeSettings();
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
    async login(context, payload: PostUserLoginRequest) {
      const { commit, dispatch, rootState, rootDispatch } = _moduleActionContext(context);
      const axiosResponse = await postLogin(payload);
      switch (axiosResponse.status) {
        case 200: {
          const response: PostUserLoginResponse = axiosResponse.data;
          const officeShortName: string = rootState.offices.allOffices.find((v) => v.id === payload.officeId)
            ?.officeShortName as string;

          //replace office in url before another actions!
          setOfficeNameIntoCurrentHref(officeShortName);

          storeToken(response.token, officeShortName);
          commit.SET_TOKEN(response.token);
          setAuthAxios(response.token);

          await dispatch.downloadPermissions();
          await dispatch.downloadUserInfo();
          if (
            typeof routerInstance.currentRoute.params.redirect !== "undefined" &&
            routerInstance.currentRoute.params.redirect !== ""
          ) {
            // noinspection ES6MissingAwait
            routerInstance.push(decodeURIComponent(routerInstance.currentRoute.params.redirect));
          } else {
            const routeWithOffice = {
              ...DEFAULT_LOGGED_ROUTE,
              ...{
                params: { officeShortName: officeShortName },
              },
            };
            // noinspection ES6MissingAwait
            routerInstance.push(routeWithOffice); // replace to remove cache after deploy
            // const route = routerInstance.resolve(routeWithOffice);
            // window.location.replace(route.href);
          }
          break;
        }
        case 400: {
          const snackbarModel = new SnackbarModel(axiosResponse.data, "error", "bottom", 3000);
          rootDispatch.popup.addSnackbar(snackbarModel);
          break;
        }
      }
    },
    async logoutCurrentOffice(context) {
      const { commit } = _moduleActionContext(context);
      const localStorageKey = createTokenStorageKey(getOfficeNameFromCurrentHref());
      const token = localStorage.getItem(localStorageKey);
      if (token !== null) {
        setAuthAxios(token);
        // noinspection ES6MissingAwait
        postLogout();
        localStorage.removeItem(localStorageKey);
      }
      commit.SET_TOKEN(null);
      commit.SET_PERMISSIONS([]);
      removeAuthAxios();
      await routerInstance.push({ name: "Login" });
    },
    async noPermissions() {
      await routerInstance.push({ name: "Insufficient Permissions" });
    },
    async logout(context) {
      const { commit, rootState } = _moduleActionContext(context);
      const offices = rootState.offices.allOffices;
      for (const office of offices) {
        const localStorageKey = createTokenStorageKey(office.officeShortName);
        const token = localStorage.getItem(localStorageKey);
        if (token !== null) {
          setAuthAxios(token);
          // noinspection ES6MissingAwait
          postLogout();
          localStorage.removeItem(localStorageKey);
        }
      }
      commit.SET_TOKEN(null);
      commit.SET_PERMISSIONS([]);
      removeAuthAxios();
      await routerInstance.push({ name: "Login" });
    },
  },
});

export default userModule;

/* eslint-disable  @typescript-eslint/no-explicit-any */
const _moduleGetterContext = (args: [any, any, any, any]) => moduleGetterContext(args, userModule);
const _moduleActionContext = (context: any) => moduleActionContext(context, userModule);
